import { Alert, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import _, { Dictionary } from 'underscore';

import Highcharts from 'highcharts';
import HC_patternFill from 'highcharts-pattern-fill';
import HighchartsReact from 'highcharts-react-official';
import getLineGraphOptions from 'utils/get-line-graph-options';

import { useAccountState } from 'contexts/account/account-context';

import { useGet } from 'hooks/use-get';

import { AxiosResponse } from 'axios';
import Dialog from 'components/dialog';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';
import Mui from 'materials';
import { useQueryClient } from '@tanstack/react-query';
import { useRouteMatch } from 'react-router-dom';
import { OwnerType } from 'types/shared/owner-type.enum';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';
import { getSiteCalculationStatus } from '../api/get-site-calculation-status';
import { getSiteFeed } from '../api/get-site-feed';
import { getSiteForecast } from '../api/get-site-forecast';

const ActionGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  height: 50,
}));

type ParamTypes = {
  localSiteId: string;
  fishGroupId: string;
  fishGroupTab: Tabs;
};
enum Tabs {
  FISH_GROUPS = 'fishgroups',
  VIRTUAL = 'virtual',
  HARVESTED = 'harvested',
}

type TableData = {
  name: string;
  date: string;
  value: number;
};

type Props = {
  siteName: string;
  globalSiteId: string;
};

HC_patternFill(Highcharts);

const SiteFeedPlan: React.FC<Props> = ({ globalSiteId }) => {
  const intl = useIntl();
  const PAGE_NAME_ID = 'planningPrognosis.siteDetails';
  const PAGE_NAME_FEED_ID = 'planningPrognosis.feedSummary';
  const theme = useTheme();
  const { account } = useAccountState();
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const previousRecalculating = useRef<boolean | undefined>();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { params } = useRouteMatch<ParamTypes>();

  // STATE
  const [periodisation, setPeriodisation] = useState(1);

  // FETCH DATA

  const { data: siteTotalFeed, isLoading: isLoadingSiteTotalFeed } = useGet(
    ['getSiteFeed', account.company?.companyId, globalSiteId, periodisation],
    ({ queryKey }) => getSiteFeed(queryKey[1] as string, queryKey[2] as string, Number(queryKey[3])),
    {
      enabled: !!globalSiteId,
    }
  );

  const { data: forecast } = useGet(
    ['getSiteForecast', globalSiteId, periodisation],
    ({ queryKey }) => getSiteForecast(String(queryKey[1]), Number(queryKey[2])),
    {
      enabled: !!globalSiteId && account.company?.features.hasPlanningPrognosisForecast,
    }
  );

  const { data: calculationStatus, refetch: refetchCalculationStatus } = useGet(
    ['getSiteCalculationStatus', account.company?.companyId, params.localSiteId],
    (context) => getSiteCalculationStatus(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!params.localSiteId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calculationStatus) return;

    if (previousRecalculating.current === true && !calculationStatus.recalculating) {
      queryClient.invalidateQueries({
        queryKey: ['getFishGroup'],
      });
    }

    previousRecalculating.current = calculationStatus.recalculating;
    setRefetchInterval(calculationStatus.recalculating ? 1000 : false);
  }, [calculationStatus, queryClient]);

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>(
    (request) => postCalculation(request.owner, request.id),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      refetchCalculationStatus();
    }
  );

  // RENDERS
  const getTableData = useCallback((): Dictionary<TableData[]> => {
    const arr: TableData[] = [];

    if (!!siteTotalFeed) {
      const actualArr: TableData[] = siteTotalFeed?.actual.map((p) => ({
        ...p,
        name: 'actual',
      }));
      arr.push(...actualArr);

      const plannedArr: TableData[] = siteTotalFeed?.planned.map((p) => ({
        ...p,
        name: 'planned',
      }));
      arr.push(...plannedArr);

      const invoicedArr: TableData[] = siteTotalFeed?.invoiced.map((p) => ({
        ...p,
        name: 'invoiced',
      }));
      arr.push(...invoicedArr);
    }

    if (!!forecast) {
      const forecastArr: TableData[] = forecast?.map((p) => ({
        ...p,
        name: 'forecast',
      }));
      arr.push(...forecastArr);
    }

    return _.groupBy(arr, (d) => d.date);
  }, [siteTotalFeed, forecast]);

  return (
    <>
      <Paper transparent sx={{ mt: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
          </Typography>
          {!calculationStatus?.recalculating && account.company?.features.hasPlanningPrognosis && (
            <Button
              disabled={startCalculation.isPending}
              sx={{ mb: 2 }}
              onClick={() => startCalculation.mutate({ owner: OwnerType.SITE, id: params.localSiteId })}>
              Recalculate
            </Button>
          )}
          {calculationStatus?.recalculating && (
            <Alert severity="success" icon={<CircularProgress size={18} />} style={{ marginBottom: 50 }}>
              <Mui.AlertTitle>
                <FormattedMessage id="planningPrognosis.alert.title" />
              </Mui.AlertTitle>
              <FormattedMessage id="planningPrognosis.alert.info" />
            </Alert>
          )}
        </Box>

        <ActionGroup>
          <Select
            size="medium"
            fullWidth={false}
            margin="none"
            label={intl.formatMessage({ id: 'profilesStrategies.label.periodisation' })}
            value={periodisation}
            style={{ minWidth: 150 }}
            onChange={(event) => setPeriodisation(Number(event.target.value))}>
            <RadioSelectItem value={1} selected={1 === periodisation}>
              <FormattedMessage id="general.week" />
            </RadioSelectItem>
            <RadioSelectItem value={2} selected={2 === periodisation}>
              <FormattedMessage id="general.month" />
            </RadioSelectItem>
          </Select>
          <Dialog
            maxWidth="sm"
            title=""
            button={
              <Button
                sx={{
                  ml: 4,
                }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.showTableBTN`} />
              </Button>
            }>
            <List
              dark
              options={[
                { id: 'date', label: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.graphDate` }), align: 'left' },
                { id: 'actual', label: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.actual` }), align: 'right' },
                { id: 'planned', label: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.planned` }), align: 'right' },
                { id: 'delivered', label: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.delivered` }), align: 'right' },
                { id: 'forecast', label: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.forecast` }), align: 'right' },
              ]}>
              {Object.entries(getTableData()).map(([key, value]) => (
                <ListRow key={key}>
                  <ListCell>
                    <FormattedDate value={key} day="2-digit" month="short" year="numeric" />
                  </ListCell>
                  <ListCell align="right">
                    <FormattedNumber
                      value={value.find((d) => d.name === 'actual')?.value ?? 0}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </ListCell>
                  <ListCell align="right">
                    <FormattedNumber
                      value={value.find((d) => d.name === 'planned')?.value ?? 0}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </ListCell>
                  <ListCell align="right">
                    <FormattedNumber
                      value={value.find((d) => d.name === 'invoiced')?.value ?? 0}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </ListCell>
                  <ListCell align="right">
                    <FormattedNumber
                      value={value.find((d) => d.name === 'forecast')?.value ?? 0}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </ListCell>
                </ListRow>
              ))}
            </List>
          </Dialog>
        </ActionGroup>
        <Paper>
          {isLoadingSiteTotalFeed && <LoadingSpinner overlay />}

          <HighchartsReact
            highcharts={Highcharts}
            options={
              {
                ...getLineGraphOptions({
                  xAxisTitle: intl.formatMessage({ id: `general.date` }),
                  yAxisTitle: intl.formatMessage({ id: `general.value` }),
                  xAxisType: 'datetime',
                  formatNumber: intl.formatNumber,
                }),
                series: isLoadingSiteTotalFeed
                  ? []
                  : ([
                      {
                        name: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.actual` }),
                        dashStyle: 'Solid',
                        color: theme.palette.bluViu.main,
                        data: siteTotalFeed?.actual.map((point) => ({
                          x: dayjs(point.date).utc(true).valueOf(),
                          y: Number(point.value.toFixed(0)),
                        })),
                      },
                      {
                        name: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.planned` }),
                        dashStyle: 'ShortDot',
                        color: theme.palette.darkBlue.main,
                        data: siteTotalFeed?.planned.map((point) => ({
                          x: dayjs(point.date).utc(true).valueOf(),
                          y: Number(point.value.toFixed(0)),
                        })),
                      },
                      {
                        name: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.delivered` }),
                        type: 'scatter',
                        color: theme.palette.secondary.main,
                        data: siteTotalFeed?.invoiced.map((point) => ({
                          x: dayjs(point.date).utc(true).valueOf(),
                          y: Number(point.value.toFixed(0)),
                          marker: {
                            fillColor: theme.palette.secondary.main,
                            enabled: true,
                            radius: 4,
                            symbol: 'circle',
                            lineWidth: 1.2,
                            states: {
                              hover: {
                                enabled: true,
                              },
                            },
                          },
                        })),
                      },
                      {
                        name: intl.formatMessage({ id: `${PAGE_NAME_FEED_ID}.forecast` }),
                        dashStyle: 'ShortDash',
                        visible: false,
                        color: theme.palette.blue.main,
                        data: forecast?.map((point) => ({ x: dayjs(point.date).utc(true).valueOf(), y: point.value })),
                      },
                    ] as Highcharts.SeriesOptionsType[]),
              } as Highcharts.Options
            }
          />
        </Paper>
      </Paper>
    </>
  );
};

export default SiteFeedPlan;
